<template>
    <VueFinalModal
        class="flex items-center justify-center p-4"
        content-class="relative flex flex-col w-full max-w-3xl bg-white dark:bg-surface-card rounded-lg shadow-2xl max-h-[90vh]"
        overlay-transition="vfm-fade"
        content-transition="vfm-fade"
        :click-to-close="state !== 'loading'"
        :esc-to-close="state !== 'loading'"
        @update:model-value="(val) => !val && emit('close')"
    >
        <div class="p-10 overflow-y-auto max-h-full" tabindex="-1">
            <div class="flex justify-between items-center mb-6 shrink-0">
                <h1 class="text-3xl font-semibold">
                    {{ state === 'done' ? 'Hotovo' : 'Chcem sa prihlásiť' }}
                </h1>
                <button 
                    v-if="state !== 'loading'"
                    aria-label="Close modal" 
                    class="text-3xl text-gray-600 dark:text-txt-body hover:text-gray-800 hover:dark:text-txt-headings transition-colors"
                    @click="emit('close')"
                >
                    ×
                </button>
            </div>
        <template v-if="state !== 'done'">
            <div>
                <p>
                    <NuxtLink
                        class="link"
                        target="_blank"
                        :to="{
                            name: 'u-id',
                            params: { id: APPLICATION_USER_ID }
                        }"
                        >Čo o mne uvidia ostatní</NuxtLink
                    >
                </p>
            </div>
            <CAlert class="mb-4" icon="info" variant="info">
                TIP: Aby sme vedeli vybrať najvhodnejšie zloženie skupiny, potrebujeme o tebe čo najviac info. Prihlás
                sa a skontroluj, či máš poriadne vyplnený svoj Životopis na GROWNi.
            </CAlert>
            <div v-if="state === 'error'" class="mb-4 text-red-500">
                {{ errors.message }}
            </div>
            <div v-else-if="state === 'loading'" class="c-spinner-wrapper">
                <CSpinner />
            </div>

            <CFormGroup :error="errors.email" label="Email" required>
                <CFormInput v-model="membershipRequest.email" autocomplete="email" />
            </CFormGroup>

            <CFormGroup :error="errors.phone" label="Telefón" required>
                <CFormInput v-model="membershipRequest.phone" autocomplete="phone" />
            </CFormGroup>

            <CFormGroup
                :error="errors.experienceLevel"
                label="Aké skúsenosti máš v oblasti, ktorej sa pracovná skupina bude venovať?"
                required
            >
                <CFormRadio v-model="membershipRequest.experienceLevel" :options="experienceLevels" />
            </CFormGroup>

            <CFormGroup :error="errors.joinMotivation" label="Čo ťa motivuje k prihláseniu sa do skupiny?" required>
                <CFormTextarea v-model="membershipRequest.joinMotivation" />
            </CFormGroup>

            <CFormGroup
                :error="errors.seniorityLevel"
                label="Ako si máš v oblasti, ktorej sa pracovná skupina bude venovať?"
                required
            >
                <CFormRadio v-model="membershipRequest.seniorityLevel" :options="seniorityLevels" />
            </CFormGroup>

            <CFormGroup :error="errors.referralSource" label="Odkiaľ vieš o pracovnej skupine?">
                <CFormRadio v-model="membershipRequest.referralSource" :options="referralSources" />
                <template v-if="membershipRequest.referralSource === 'other'">
                    <CFormTextarea
                        v-model="membershipRequest.referralSourceOther"
                        class="mt-2"
                        placeholder="Prosím, upresni zdroj"
                    />
                </template>
            </CFormGroup>

            <div class="text-sm italic text-gray-500 dark:text-txt-body">
                Beriem na vedomie, že moje hore uvedené údaje<span v-if="APPLICATION_USER_PROFILE"> / profil,</span>
                poskytne prevádzkovateľ portálu GROWNi ambasádorovi skupiny za účelom organizácie a účasti v skupine
                <strong>{{ skillTitle }}</strong
                >. Viac o <a class="link" href="/privacy" target="_blank">spracovaní osobných údajov</a>.
            </div>
        </template>

        <template v-else>
            <p>Ďakujeme za tvoju registráciu (na tvoj e-mail sme zaslali ďalšie informácie).</p>
        </template>

            <div class="mt-6 flex w-full shrink-0">
                <CButton
                    v-if="state !== 'done'"
                    class="ml-auto w-full sm:w-auto"
                    :disabled="state === 'loading'"
                    size="large"
                    variant="primary"
                    @click.prevent="submit"
                >
                    Odoslať záujem
                </CButton>
                <CButton
                    v-else
                    class="ml-auto w-full sm:w-auto"
                    size="large"
                    variant="primary-150"
                    @click.prevent="emit('close', membershipRequest.value)"
                >
                    OK
                </CButton>
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { fetchSignInMethodsForEmail } from 'firebase/auth'
import type { ValidationErrors, ValidationResults } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'
import { ref, computed, onMounted } from 'vue'
import { useHead, useNuxtApp } from '#imports'
import { validateMembershipRequest } from '@/validators/skill'
import { VueFinalModal } from 'vue-final-modal'

import { experienceLevels, referralSources, seniorityLevels } from '#shared/types/dictionary'
import type { LoadingState } from '#shared/types/LoadingState'
import type { MembershipRequest } from '#shared/types/MembershipRequest'
import type { Skill } from '#shared/types/Skill'
import type { UserPrivateProfile, UserProfile } from '#shared/types/User'
import { useHeadMeta } from '@/composables/head/useHeadMeta'
import { useUser } from '@/composables/user/useUser'
import { EntityType } from '#shared/types/EntityType'
import { OrgTransactionStatus, OrgWithTransactionBalance } from '#shared/types/OrgTransaction'
import { RelatedItems } from '#shared/types/RelatedItems'
import { GetTalentBaseUserProfile } from '#shared/types/TalentBaseRequest'
import { getFullName } from '#shared/utils'
import BuyCreditsModal, { type TBuyCreditsClose } from '@/components/payment/modals/BuyCreditsModal.vue'
import SuccessCreditsModal from '@/components/payment/modals/SuccessCreditsModal.vue'
import SummaryCreditsModal, { type TSummaryCreditsClose } from '@/components/payment/modals/SummaryCreditsModal.vue'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useJobs } from '@/composables/jobs/useJobs'
import { usePayment } from '@/composables/payment/usePayment'
import { useSharing } from '@/composables/sharing/useSharing'
import { useMembershipRequests } from '@/composables/membershipRequests/useMembershipRequests'
import { useSkills } from '@/composables/skills/useSkills'
import { useTalentBase } from '@/composables/talentBase/useTalentBase'
import { isProduction } from '@/utils/helpers'

const props = defineProps({
    skillId: {
        type: String,
        required: true
    }
})

const { getPaymentSuccessData } = usePayment()
const { getSharingUrl } = useSharing()
const { searchJobs } = useJobs()
const { serializeFilters } = useTypesense()
const { createMembershipRequest } = useMembershipRequests()
const { loadSkill } = useSkills()

const emit = defineEmits(['close'])
const { $firebase, $auth } = useNuxtApp()
const auth = $auth
const { gtag } = useGtag()

const store = useApplicationStore()
const APPLICATION_USER_ID = computed(() => store.userId)
const APPLICATION_USER_PROFILE = computed(() => store.userProfile)
const APPLICATION_USER_PRIVATE_PROFILE = computed(() => store.userPrivateProfile)
const { headMetaTrackGTM } = useHeadMeta()
const { updateUserPrivateProfile } = useUser()

const skill = ref<Skill>()
const userProfile = ref<UserProfile | undefined>(undefined)
const membershipRequest = ref<Partial<MembershipRequest>>({})
const state = ref<LoadingState>('idle')
const errors = ref<ValidationErrors>({})
const registerUser = ref(false)
const password = ref<string | undefined>()
const trackGtmEvent = ref(false)
const referralSourceOther = ref<string | undefined>()

async function fetchData() {
    skill.value = await loadSkill(props.skillId)

    const userPrivateProfile: UserPrivateProfile = cloneDeep(APPLICATION_USER_PRIVATE_PROFILE.value || {})
    userProfile.value = cloneDeep(APPLICATION_USER_PROFILE.value || {})

    if (userProfile.value?.firstName != null) {
        membershipRequest.value.firstName = userProfile.value.firstName
    }

    if (userProfile.value?.lastName != null) {
        membershipRequest.value.lastName = userProfile.value.lastName
    }

    if (userPrivateProfile?.contactEmail) {
        membershipRequest.value.email = userPrivateProfile.contactEmail
    }

    if (userPrivateProfile?.phone) {
        membershipRequest.value.phone = userPrivateProfile.phone
    }
}

fetchData()

const skillTitle = computed(() => skill.value?.title)

useHead(() => ({
    ...headMetaTrackGTM(trackGtmEvent.value ? "event: 'user_request', request_type: 'study_group'" : '')
}))
async function validate(): Promise<boolean> {
    const results: ValidationResults = await validateMembershipRequest(membershipRequest.value)

    if (registerUser.value) {
        if (!password.value) {
            results.isValid = false
            results.errors.password = 'Povinný údaj.'
        } else if (await isEmailInUse(membershipRequest.value.email || '')) {
            results.isValid = false
            results.errors.message =
                'Tento email už je registrovaný na GROWNi. Môže ho používať iba prihlásený používateľ.'
        }
    }

    errors.value = results.errors

    return results.isValid
}

async function submit() {
    try {
        state.value = 'loading'

        if (!(await validate())) {
            state.value = 'error'
            return
        }

        membershipRequest.value = await createMembershipRequest(
            props.skillId,
            { ...membershipRequest.value },
            userProfile.value?.id
        )

        if (process.client) {
            gtag('event', 'submitApplicationModal', {
                event_category: 'membership'
            })
        }

        trackGtmEvent.value = true

        if (userProfile.value?.id && membershipRequest.value.phone)
            await updateUserPrivateProfile(userProfile.value.id, { phone: membershipRequest.value.phone })

        state.value = 'done'
        
        setTimeout(() => {
            emit('close', membershipRequest.value)
        }, 2000)
    } catch (e) {
        state.value = 'error'
    }
}

function reloadProfile() {
    return store.dispatch('reloadUserProfile')
}

async function isEmailInUse(email: string) {
    const methodsForEmail = await fetchSignInMethodsForEmail(auth, email)
    return methodsForEmail.length
}


</script>

